import React from 'react';
import { Box, Divider, IconButton, Popover, Typography } from '@mui/material';
import CloseOutlinedIcon from '@mui/icons-material/CloseOutlined';
import LinkOffRoundedIcon from '@mui/icons-material/LinkOffRounded';
import { formatDateShortMonth, formatTimeFromISO } from '../../../utils/dateUtils';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface DelinkLogData {
  id: number;
  action: string;
  recipient_patient_id: number | null;
  recipient_name: string | null;
  recipient_mrn: string | null;
  reason: string | null;
  created_on: string | null;
  created_by: string | null;
}

export interface DelinkLogPopoverProps {
  anchorEl: HTMLElement | null;
  open: boolean;
  data: DelinkLogData | null;
  onClose: () => void;
}

// ---------------------------------------------------------------------------
// Info row helper
// ---------------------------------------------------------------------------

const InfoRow: React.FC<{ label: string; value: string }> = ({ label, value }) => (
  <Box sx={{ display: 'flex', alignItems: 'baseline', justifyContent: 'space-between', py: 0.35 }}>
    <Typography variant="body2" sx={{ color: '#667085', fontSize: '0.78rem', flexShrink: 0, mr: 2 }}>
      {label}
    </Typography>
    <Typography variant="body2" sx={{ color: '#344054', fontWeight: 600, fontSize: '0.8rem', textAlign: 'right' }}>
      {value}
    </Typography>
  </Box>
);
// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

const DelinkLogPopover: React.FC<DelinkLogPopoverProps> = ({
  anchorEl,
  open,
  data,
  onClose,
}) => (
  <Popover
    anchorEl={anchorEl}
    open={open}
    onClose={onClose}
    anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
    transformOrigin={{ vertical: 'top', horizontal: 'left' }}
    slotProps={{
      paper: {
        sx: {
          borderRadius: 2,
          minWidth: 280,
          maxWidth: 340,
          p: 0,
          boxShadow: 4,
        },
      },
    }}
    data-testid="invoice-delink-log-popover"
  >
    {/* Header */}
    <Box
      sx={{
        px: 1.5,
        py: 1,
        borderBottom: '1px solid',
        borderColor: 'divider',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        bgcolor: 'grey.50',
      }}
    >
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75 }}>
        <LinkOffRoundedIcon sx={{ fontSize: 16, color: '#BF360C' }} />
        <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>
          Delink Info
        </Typography>
      </Box>
      <IconButton size="small" onClick={onClose} aria-label="close delink info">
        <CloseOutlinedIcon fontSize="small" />
      </IconButton>
    </Box>

    {/* Body */}
    {data ? (
      <Box sx={{ px: 1.5, py: 1.25 }}>
        {/* Recipient */}
        <InfoRow label="Recipient MRN" value={data.recipient_mrn || '-'} />
        <Divider sx={{ my: 0.75 }} />

        {/* Action details */}
        <InfoRow label="Date" value={data.created_on ? `${formatDateShortMonth(data.created_on)}, ${formatTimeFromISO(data.created_on)}` : '-'} />
        <InfoRow label="By" value={data.created_by || '-'} />

        {/* Reason — only show if present, with wrapping support */}
        {data.reason && (
          <>
            <Divider sx={{ my: 0.75 }} />
            <Typography variant="caption" sx={{ color: '#667085', fontWeight: 500, display: 'block', mb: 0.25 }}>
              Reason
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: 'text.primary',
                fontWeight: 500,
                lineHeight: 1.5,
                whiteSpace: 'pre-wrap',
                wordBreak: 'break-word',
                maxHeight: 120,
                overflowY: 'auto',
              }}
            >
              {data.reason}
            </Typography>
          </>
        )}
      </Box>
    ) : (
      <Box sx={{ px: 1.5, py: 2, textAlign: 'center' }}>
        <Typography variant="body2" sx={{ color: 'text.disabled' }}>
          No delink information available
        </Typography>
      </Box>
    )}
  </Popover>
);


export default DelinkLogPopover;
